"use client";

import Link from "next/link";
import { useEffect, useState, type ReactNode } from "react";
import { api, type ActivityItem } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { formatRelative } from "@/lib/format";
import { StatsCard } from "@/components/dashboard/stats-ui";

// The activity feed: the shared row look, and the Recent Activities card used
// on Home and Stations. Rows come from the API's activity log, already limited
// to the categories the signed-in account may see.

export const CATEGORY_STYLE: Record<string, { label: string; color: string; bg: string; icon: ReactNode }> = {
  media: { label: "Media", color: "#9333ea", bg: "#f3e8ff", icon: <path d="M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" /> },
  playlist: { label: "Playlists", color: "#2563eb", bg: "#dbeafe", icon: <path d="M3 6h13M3 12h13M3 18h9M19 8v8l-3-3" /> },
  schedule: { label: "Schedule", color: "#0d9488", bg: "#ccfbf1", icon: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></> },
  live: { label: "Live", color: "#dc2626", bg: "#fee2e2", icon: <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v1a7 7 0 0 1-14 0v-1M12 18v4" /> },
  settings: { label: "Settings", color: "#ea580c", bg: "#ffedd5", icon: <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></> },
  user: { label: "Users", color: "#16a34a", bg: "#dcfce7", icon: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /></> },
  role: { label: "Roles", color: "#16a34a", bg: "#dcfce7", icon: <path d="M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6z" /> },
  request: { label: "Requests", color: "#9333ea", bg: "#f3e8ff", icon: <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.5L3 21l2-5.6A8.5 8.5 0 1 1 21 11.5z" /> },
  auth: { label: "Sign-ins", color: "#475569", bg: "#e2e8f0", icon: <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" /> },
  system: { label: "System", color: "#dc2626", bg: "#fee2e2", icon: <><rect x="3" y="4" width="18" height="6" rx="1" /><rect x="3" y="14" width="18" height="6" rx="1" /></> },
};

const FALLBACK_STYLE = CATEGORY_STYLE.system;

// Failed or blocked sign-ins and a stream going offline read as warnings.
export function isWarning(item: ActivityItem): boolean {
  return item.action === "auth.login_failed" || item.action === "auth.login_blocked" || item.action === "stream.offline";
}

export function CategoryDot({ category, warn = false, size = "h-8 w-8" }: { category: string; warn?: boolean; size?: string }) {
  const style = warn ? CATEGORY_STYLE.system : (CATEGORY_STYLE[category] ?? FALLBACK_STYLE);
  return (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full`} style={{ backgroundColor: style.bg, color: style.color }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
        {style.icon}
      </svg>
    </span>
  );
}

export function ActivityRow({ item }: { item: ActivityItem }) {
  return (
    <li className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
      <CategoryDot category={item.category} warn={isWarning(item)} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium" title={item.summary}>
          {item.summary}
        </div>
        <div className="truncate text-xs text-text-tertiary">{item.actor_name ?? "—"}</div>
      </div>
      <div className="shrink-0 text-xs text-text-tertiary" title={new Date(item.created_at).toLocaleString()}>
        {formatRelative(item.created_at)}
      </div>
    </li>
  );
}

export function RecentActivitiesCard({
  subtitle = "What's happened across the station.",
  limit = 5,
  className = "",
}: {
  subtitle?: string;
  limit?: number;
  className?: string;
}) {
  const { can } = useAuth();
  const [items, setItems] = useState<ActivityItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.activity({ limit });
        if (!cancelled) setItems(r.items);
      } catch {
        if (!cancelled) setItems((prev) => prev ?? []);
      }
    }
    const first = setTimeout(load, 0);
    const poll = setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(poll);
    };
  }, [limit]);

  return (
    <StatsCard
      className={className}
      icon={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>}
      iconBg="#0d9488"
      title="Recent Activities"
      subtitle={subtitle}
      availability="live"
    >
      {items === null ? (
        <p className="text-xs text-text-tertiary">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-text-tertiary">
          Nothing yet. Uploads, playlist and schedule changes, sign-ins and stream status show up here as they happen.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-light">
          {items.map((i) => (
            <ActivityRow key={i.id} item={i} />
          ))}
        </ul>
      )}
      {can("monitoring.view") && (
        <Link href="/logs" className="text-xs font-semibold text-accent-blue">
          View all logs →
        </Link>
      )}
    </StatsCard>
  );
}
